/**
 * Master Admin notification tone.
 * Browsers often skip OS sounds for in-page Notification() / FCM foreground
 * messages, so we synthesize a short chime with Web Audio.
 */

let audioCtx: AudioContext | null = null;
let unlockAttached = false;
let lastPlayedAt = 0;

const MIN_INTERVAL_MS = 700;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) {
    audioCtx = new Ctx();
  }
  return audioCtx;
}

/** Call after login / first click so autoplay policies allow later tones. */
export function unlockAdminNotificationAudio(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => {
      /* ignore */
    });
  }
}

/** Attach one-time gesture listeners so the first click/key unlocks audio. */
export function ensureAdminNotificationAudioUnlock(): () => void {
  if (typeof window === 'undefined' || unlockAttached) {
    return () => {};
  }
  unlockAttached = true;

  const unlock = () => {
    unlockAdminNotificationAudio();
  };

  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock, { passive: true });
  // Login already interacted; try resume immediately as well.
  unlock();

  return () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    unlockAttached = false;
  };
}

/**
 * Play a short two-note alert chime.
 * Safe to call from FCM foreground handler or SW postMessage.
 */
export function playAdminNotificationTone(): void {
  const now = Date.now();
  if (now - lastPlayedAt < MIN_INTERVAL_MS) return;
  lastPlayedAt = now;

  const ctx = getAudioContext();
  if (!ctx) return;

  const start = () => {
    try {
      const t0 = ctx.currentTime;
      const notes = [
        { freq: 880, at: 0, dur: 0.12 },
        { freq: 1174.66, at: 0.14, dur: 0.18 },
      ];

      for (const note of notes) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, t0 + note.at);
        gain.gain.setValueAtTime(0.0001, t0 + note.at);
        gain.gain.exponentialRampToValueAtTime(0.22, t0 + note.at + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + note.at + note.dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t0 + note.at);
        osc.stop(t0 + note.at + note.dur + 0.02);
      }
    } catch (err) {
      console.warn('[Admin FCM] Notification tone failed:', err);
    }
  };

  if (ctx.state === 'suspended') {
    void ctx.resume().then(start).catch(() => {
      /* autoplay blocked until user gesture */
    });
    return;
  }
  start();
}
